import { IPartialWorkflowStructure } from "@/components/Spotflow/Editor/usePartialRun";
import { SpotToast } from "@/components/SpotToast";
import {
  IsValidResponse,
  NodeDefinition,
  NodesService,
  UpdateWorkflowRequest,
  WorkflowService,
} from "@/generated";
import {
  IODefinition,
  IORules,
  NoxusReactflowNode,
  SaveState,
} from "@/types/spotflow";
import { findMaxStringValue } from "@/utils/generic";
import { ANALYTICS_EVENTS, analytics_track } from "@/utils/mixpanel";
import { DEFAULT_RUN_SETTINGS, type RunSettings } from "@/lib/run-settings";
import { atom } from "jotai";
import { UseFormReturn } from "react-hook-form";
import { Edge, XYPosition, ReactFlowInstance, Connection } from "@xyflow/react";
import { v4 as uuidv4 } from "uuid";
import { atomWithStorage, createJSONStorage, selectAtom } from "jotai/utils";
import { fieldTransformers } from "./utils";
import { createFlowDefinition } from "@/components/Spotflow/Editor/useEditorControls";
import {
  getBoundingBox,
  classifyEdges,
  buildSubflowAndInvokeNode,
  GROUP_PADDING,
} from "@/utils/group-utils";

// Define initial states
const initialNodes: NoxusReactflowNode[] = [];
const initialEdges: Edge[] = [];
const initialIsValidData: IsValidResponse | undefined = undefined;
const initialRunErrorMapping: Record<string, any> = {};

// Create atoms with initial states
export const runSettingsAtom = atom<RunSettings>(DEFAULT_RUN_SETTINGS);

export const nodesAtom = atom<NoxusReactflowNode[]>(initialNodes);
export const edgesAtom = atom<Edge[]>(initialEdges);

const setsAreEqual = (a: Set<string>, b: Set<string>) => {
  if (a.size !== b.size) return false;
  const aArray = Array.from(a);
  for (const item of aArray) {
    if (!b.has(item)) return false;
  }
  return true;
};

export const nodeTypesSetAtom = selectAtom(
  nodesAtom,
  (nodes) => new Set(nodes.map((n) => n.data.type).filter(Boolean) as string[]),
  setsAreEqual
);
export const isValidDataAtom = atom<IsValidResponse | undefined>(
  initialIsValidData
);
export const runErrorMappingAtom = atom<Record<string, any>>(
  initialRunErrorMapping
);

export const activeFlowIdAtom = atom<string | undefined>(undefined);

export const saveWorkflowHashAtom = atom<string | undefined>(undefined);

export const saveStateAtom = atom<SaveState>({
  timestamp: undefined,
  status: "not_saved",
});

export const openDropdownMenuAtom = atom<string | null>(null);

interface IOpenWorkflow {
  id: string;
  name: string;
  icon?: string;
  errorHandlerId?: string;
  type?: string;
  version?: string;
}

export const areConfigEndpointsLoadedAtom = atom<boolean>(false);

// True when the editor is mounted in read-only mode (viewer permissions or
// older version). Components inside the editor tree should disable
// edit-time queries/mutations when this is true. Set from Editor.tsx.
export const editorReadOnlyAtom = atom<boolean>(false);

export const resetEditorAtoms = atom(
  null,
  (get, set, args: { ignoreWorkflowHash?: boolean } = {}) => {
    set(runSettingsAtom, DEFAULT_RUN_SETTINGS);
    set(nodesAtom, initialNodes);
    set(edgesAtom, initialEdges);
    set(isValidDataAtom, initialIsValidData);
    set(runErrorMappingAtom, initialRunErrorMapping);
    set(activeFlowIdAtom, undefined);
    set(hasQueueToSaveAtom, false);
    set(saveWorkflowHashAtom, undefined);
    set(saveStateAtom, {
      timestamp: undefined,
      status: "not_saved",
    });
    set(areConfigEndpointsLoadedAtom, false);
    set(editorReadOnlyAtom, false);
  }
);

export const activeHandlesDictAtom = atom((get) => {
  const edges = get(edgesAtom);

  return edges.reduce(
    (
      dictionary: Record<string, { inputs: string[]; outputs: string[] }>,
      obj: Edge
    ) => {
      dictionary[obj.source] = dictionary[obj.source] || {
        inputs: [],
        outputs: [],
      };
      dictionary[obj.target] = dictionary[obj.target] || {
        inputs: [],
        outputs: [],
      };

      /* The source of an edge is the output of a node and vice versa */
      dictionary[obj.source].outputs.push(obj.sourceHandle as string);
      dictionary[obj.target].inputs.push(obj.targetHandle as string);

      return dictionary;
    },
    {}
  );
});

const updateNodeAtom = atom(
  null,
  (
    get,
    set,
    args: {
      id: string;
      node: NoxusReactflowNode;
      options?: { clearDanglingEdges?: boolean };
    }
  ) => {
    const { id, node, options } = args;
    if (id !== node.id) return;

    const nodes = get(nodesAtom);
    const edges = get(edgesAtom);

    const new_nodes = nodes.map((n) => {
      if (n.id === id) {
        return node;
      }
      return n;
    });

    set(nodesAtom, new_nodes);

    if (options?.clearDanglingEdges) {
      const initial_edges_count = edges.length;
      // Note: isValidConnection would need to be implemented or passed in
      const new_edges = edges.filter((e) => {
        // Basic connection validation
        const sourceNode = new_nodes.find((n) => n.id === e.source);
        const targetNode = new_nodes.find((n) => n.id === e.target);
        return sourceNode && targetNode;
      });

      const should_ignore_edge_deletion =
        new_edges.length === 0 && initial_edges_count > 5;

      if (should_ignore_edge_deletion) {
        console.warn("Potential edge deletion issue detected", {
          nodes,
          edges,
        });
      } else {
        set(edgesAtom, new_edges);
      }
    }
  }
);

export const addNewDynamicInputAtom = atom(
  null,
  (
    get,
    set,
    args: {
      nodeId: string;
      connector_name: string;
      type?: string;
      is_list?: boolean;
    }
  ) => {
    const { nodeId, connector_name } = args;
    const nodes = get(nodesAtom);
    const node = nodes.find((n) => n.id === nodeId);
    if (!node) return;

    const multi_connector = node.data.config.inputs.find(
      (i: IODefinition) => i.name === connector_name
    );
    const copyNode = structuredClone(node);
    const maxInputValue = multi_connector?.keys
      ? findMaxStringValue(multi_connector?.keys, /^Input (\d+)$/)
      : 0;

    if (multi_connector?.type === "variable_type_size_connector") {
      const new_keys: IODefinition[] = copyNode.data.config.inputs.map(
        (i: IODefinition) => {
          if (i.name === connector_name) {
            const newKey = `Input ${maxInputValue + 1}`;
            return {
              ...i,
              keys: [...(i.keys || []), newKey],
              type_definitions: {
                ...(i.type_definitions || {}),
                [newKey]: {
                  data_type: args.type || "str",
                  is_list: args.is_list ?? i.is_list,
                },
              },
              gathered_keys: args.is_list
                ? [...(i.gathered_keys || []), newKey]
                : i.gathered_keys,
            };
          }
          return i;
        }
      );
      copyNode.data.config.inputs = new_keys;
    } else {
      const new_keys: IODefinition[] = copyNode.data.config.inputs.map(
        (i: IODefinition) => {
          if (i.name === connector_name) {
            return {
              ...i,
              keys: [...(i.keys || []), `Input ${maxInputValue + 1}`],
            };
          }
          return i;
        }
      );
      copyNode.data.config.inputs = new_keys;
    }

    set(updateNodeAtom, {
      id: nodeId,
      node: copyNode,
    });

    set(hasQueueToSaveAtom, true);
  }
);

export const addNewDynamicOutputAtom = atom(
  null,
  (
    get,
    set,
    args: { nodeId: string; connector_name: string; type?: string }
  ) => {
    const { nodeId, connector_name } = args;
    const nodes = get(nodesAtom);
    const node = nodes.find((n) => n.id === nodeId);
    if (!node) return;

    const multi_connector = node.data.config.outputs.find(
      (i: IODefinition) => i.name === connector_name
    );
    const copyNode = structuredClone(node);
    const maxOutputValue = multi_connector?.keys
      ? findMaxStringValue(multi_connector?.keys, /^Output (\d+)$/)
      : 0;

    const new_keys: IODefinition[] = copyNode.data.config.outputs.map(
      (i: IODefinition) => {
        if (i.name === connector_name) {
          return {
            ...i,
            keys: [...(i.keys || []), `Output ${maxOutputValue + 1}`],
          };
        }
        return i;
      }
    );
    copyNode.data.config.outputs = new_keys;

    set(updateNodeAtom, {
      id: nodeId,
      node: copyNode,
    });

    set(hasQueueToSaveAtom, true);
  }
);

export const removeDynamicOutputAtom = atom(
  null,
  (get, set, args: { nodeId: string; key: string; connector_name: string }) => {
    const { nodeId, key, connector_name } = args;
    const nodes = get(nodesAtom);
    const node = nodes.find((n) => n.id === nodeId);
    if (!node) return;

    const copyNode = structuredClone(node);

    copyNode.data.config.outputs = (
      copyNode.data.config.outputs as IODefinition[]
    ).map((i) => {
      if (i.name === connector_name) {
        return { ...i, keys: i.keys?.filter((k) => k !== key) || [] };
      }
      return i;
    });

    set(edgesAtom, (edges) =>
      edges.filter(
        (e) =>
          e.source !== nodeId || e.sourceHandle !== `${connector_name}::${key}`
      )
    );

    set(updateNodeAtom, {
      id: nodeId,
      node: copyNode,
    });
  }
);

export const removeDynamicInputAtom = atom(
  null,
  (get, set, args: { nodeId: string; key: string; connector_name: string }) => {
    const { nodeId, key, connector_name } = args;
    const nodes = get(nodesAtom);
    const node = nodes.find((n) => n.id === nodeId);
    if (!node) return;

    const copyNode = structuredClone(node);
    copyNode.data.config.inputs = (
      copyNode.data.config.inputs as IODefinition[]
    ).map((i) => {
      if (i.name === connector_name) {
        return {
          ...i,
          keys: i.keys?.filter((k) => k !== key) || [],
          gathered_keys: i.gathered_keys?.filter((k) => k !== key),
        };
      }
      return i;
    });

    set(edgesAtom, (edges) =>
      edges.filter(
        (e) =>
          e.target !== nodeId || e.targetHandle !== `${connector_name}::${key}`
      )
    );

    set(updateNodeAtom, {
      id: nodeId,
      node: copyNode,
      options: {
        clearDanglingEdges: true,
      },
    });
  }
);

export const changeDynamicInputNameAtom = atom(
  null,
  (
    get,
    set,
    args: {
      nodeId: string;
      oldName: string;
      newName: string;
      connector_name: string;
      form: UseFormReturn | undefined;
    }
  ) => {
    const { nodeId, oldName, newName: rawNewName, connector_name, form } = args;
    const newName = rawNewName.replaceAll('"', "");
    const nodes = get(nodesAtom);
    const node = nodes.find((n) => n.id === nodeId);
    if (!node || oldName === newName) return oldName;

    const copyNode = structuredClone(node);
    const multi_connector: IODefinition | undefined =
      node.data.config.inputs.find(
        (i: IODefinition) => i.name === connector_name
      );

    if (newName.length === 0) {
      return oldName;
    }

    if (!multi_connector?.keys) {
      return "";
    }

    if (multi_connector.keys.includes(newName)) {
      return oldName;
    }

    copyNode.data.config.inputs = (
      copyNode.data.config.inputs as IODefinition[]
    ).map((i) => {
      if (i.name !== connector_name) {
        return i;
      }

      const updatedDefinition: IODefinition = {
        ...i,
        keys: i.keys!.map((k) => (k === oldName ? newName : k)),
        gathered_keys: i.gathered_keys?.map((k) =>
          k === oldName ? newName : k
        ),
      };

      if (i.type_definitions && i.type_definitions[oldName]) {
        const { [oldName]: oldTypeDef, ...restTypeDefs } = i.type_definitions;
        updatedDefinition.type_definitions = {
          ...restTypeDefs,
          [newName]: oldTypeDef,
        };
      }

      return updatedDefinition;
    });

    set(edgesAtom, (edges) =>
      edges.map((e) => {
        if (
          e.target === nodeId &&
          e.targetHandle === `${connector_name}::${oldName}`
        ) {
          return {
            ...e,
            targetHandle: `${connector_name}::${newName}`,
          };
        }
        return e;
      })
    );

    set(updateNodeAtom, {
      id: nodeId,
      node: copyNode,
    });

    if (!form) return newName;

    // This timeout makes sure that form.watch on useEditorControls
    // only runs when the nodes have been updated.
    // Otherwise, the form.watch will run before the nodes have been updated,
    // and the config_endpoint will be called with the wrong values.
    setTimeout(() => {
      Object.entries(node.data.config.config).forEach(([fieldKey, config]) => {
        const displayType = (config as any).display?.type;
        const transformer = fieldTransformers[displayType];

        if (transformer) {
          const currentValue = form.getValues(`${nodeId}.${fieldKey}`);
          const transformedValue = transformer(
            currentValue,
            oldName,
            newName,
            nodeId,
            fieldKey
          );
          form.setValue(`${nodeId}.${fieldKey}`, transformedValue);
        }
      });
    }, 1);

    set(hasQueueToSaveAtom, true);
    return newName;
  }
);

export const changeDynamicOutputNameAtom = atom(
  null,
  (
    get,
    set,
    args: {
      nodeId: string;
      oldName: string;
      newName: string;
      connector_name: string;
    }
  ) => {
    const { nodeId, oldName, newName: rawNewName, connector_name } = args;
    const newName = rawNewName.replaceAll('"', "");
    const nodes = get(nodesAtom);
    const node = nodes.find((n) => n.id === nodeId);
    if (!node) return "";
    if (newName.includes('"')) {
      return oldName;
    }

    const copyNode = structuredClone(node);
    const multi_connector: IODefinition | undefined =
      node.data.config.outputs.find(
        (i: IODefinition) => i.type === "variable_connector"
      );

    if (!multi_connector?.keys) {
      return "";
    }

    if (multi_connector.keys.includes(newName)) {
      return oldName;
    }

    copyNode.data.config.outputs = (
      copyNode.data.config.outputs as IODefinition[]
    ).map((i) => {
      if (i.name !== connector_name) {
        return i;
      }

      const updatedDefinition: IODefinition = {
        ...i,
        keys: i.keys!.map((k) => (k === oldName ? newName : k)),
      };

      if (i.type_definitions && i.type_definitions[oldName]) {
        const { [oldName]: oldTypeDef, ...restTypeDefs } = i.type_definitions;
        updatedDefinition.type_definitions = {
          ...restTypeDefs,
          [newName]: oldTypeDef,
        };
      }

      return updatedDefinition;
    });

    set(updateNodeAtom, {
      id: nodeId,
      node: copyNode,
      options: {
        clearDanglingEdges: true,
      },
    });

    set(edgesAtom, (edges) =>
      edges.map((e) => {
        if (
          e.source === nodeId &&
          e.sourceHandle === `${connector_name}::${oldName}`
        ) {
          return {
            ...e,
            sourceHandle: `${connector_name}::${newName}`,
          };
        }
        return e;
      })
    );

    set(hasQueueToSaveAtom, true);
    return newName;
  }
);

export const changeDynamicInputTypeAtom = atom(
  null,
  (
    get,
    set,
    args: {
      nodeId: string;
      keyName: string;
      connector_name: string;
      newType: string;
      newIsList?: boolean;
    }
  ) => {
    const { nodeId, keyName, connector_name, newType, newIsList } = args;
    const nodes = get(nodesAtom);
    const node = nodes.find((n) => n.id === nodeId);
    if (!node) return;

    const copyNode = structuredClone(node);

    copyNode.data.config.inputs = (
      copyNode.data.config.inputs as IODefinition[]
    ).map((i) => {
      if (i.name !== connector_name) {
        return i;
      }

      if (i.type_definitions && i.type_definitions[keyName]) {
        const withoutKey = (i.gathered_keys || []).filter((k) => k !== keyName);
        return {
          ...i,
          type_definitions: {
            ...i.type_definitions,
            [keyName]: {
              ...i.type_definitions[keyName],
              data_type: newType,
              is_list: newIsList ?? i.type_definitions[keyName].is_list,
            },
          },
          gathered_keys: newIsList ? [...withoutKey, keyName] : withoutKey,
        };
      }

      return i;
    });

    set(edgesAtom, (edges) =>
      edges.filter(
        (e) =>
          e.target !== nodeId ||
          e.targetHandle !== `${connector_name}::${keyName}`
      )
    );

    set(updateNodeAtom, {
      id: nodeId,
      node: copyNode,
      options: {
        clearDanglingEdges: true,
      },
    });

    set(hasQueueToSaveAtom, true);
  }
);

export const changeDynamicOutputTypeAtom = atom(
  null,
  (
    get,
    set,
    args: {
      nodeId: string;
      keyName: string;
      connector_name: string;
      newType: string;
    }
  ) => {
    const { nodeId, keyName, connector_name, newType } = args;
    const nodes = get(nodesAtom);
    const node = nodes.find((n) => n.id === nodeId);
    if (!node) return;

    const copyNode = structuredClone(node);

    copyNode.data.config.outputs = (
      copyNode.data.config.outputs as IODefinition[]
    ).map((i) => {
      if (i.name !== connector_name) {
        return i;
      }

      if (i.type_definitions && i.type_definitions[keyName]) {
        return {
          ...i,
          type_definitions: {
            ...i.type_definitions,
            [keyName]: {
              ...i.type_definitions[keyName],
              data_type: newType,
            },
          },
        };
      }

      return i;
    });

    set(edgesAtom, (edges) =>
      edges.filter(
        (e) =>
          e.source !== nodeId ||
          e.sourceHandle !== `${connector_name}::${keyName}`
      )
    );

    set(updateNodeAtom, {
      id: nodeId,
      node: copyNode,
      options: {
        clearDanglingEdges: true,
      },
    });

    set(hasQueueToSaveAtom, true);
  }
);

export const addNewNoteNodeAtom = atom(
  null,
  (
    get,
    set,
    args: {
      reactFlowInstance?: ReactFlowInstance;
      position?: { x: number; y: number };
    }
  ) => {
    const { reactFlowInstance, position } = args;
    const getRandomNumber = () => Math.floor(Math.random() * 100 + 1);

    const newNode: NoxusReactflowNode = {
      id: uuidv4(),
      type: "note",
      position: position ||
        reactFlowInstance?.screenToFlowPosition({
          x: (window?.innerWidth / 2 || 500) + getRandomNumber(),
          y: (window?.innerHeight / 2 || 500) + getRandomNumber(),
        }) || {
          x: 500 + getRandomNumber(),
          y: 500 + getRandomNumber(),
        },
      style: {
        height: 200,
        width: 220,
        zIndex: -2,
      },
      deletable: true,
      data: {
        name: "Sticky Note",
        type: "StickyNoteNode",
        resizable: true,
        config: {},
      },
    };

    const setNodes = (nodes: NoxusReactflowNode[]) => set(nodesAtom, nodes);
    const nodes = get(nodesAtom);

    setNodes([...nodes, newNode]);
  }
);

export const addNewNodeAtom = atom(
  null,
  async (
    get,
    set,
    args: {
      type: string;
      position?: XYPosition | undefined;
      nodesDefinition: NodeDefinition[] | undefined;
      reactFlowInstance: ReactFlowInstance | undefined;
      form: UseFormReturn | undefined;
      config?: Record<string, any>;
      groupId: string;
      workflowType?: string;
    }
  ) => {
    const {
      type,
      position,
      nodesDefinition,
      reactFlowInstance,
      form,
      config,
      groupId,
      workflowType,
    } = args;
    const isTutorial = get(isTutorialAtom);

    const nodes = get(nodesAtom);

    const setNodes = (nodes: NoxusReactflowNode[]) => set(nodesAtom, nodes);
    const setHasQueueToUpdateConnectors = (nodeId: string) =>
      set(hasQueueToUpdateConnectorsAtom, nodeId);

    const setHasQueueToSave = (value: boolean) =>
      set(hasQueueToSaveAtom, value);

    if (!type) return;
    const nd = nodesDefinition?.find((n) => n.type === type);

    if (
      workflowType &&
      nd?.allowed_in_flow_types &&
      !nd.allowed_in_flow_types.includes(workflowType)
    ) {
      SpotToast.error(`${nd.title} node is not allowed in this workflow type`);
      return;
    }

    // Check if node is unique and already exists
    if (nd?.unique) {
      const existingNode = nodes.find((n) => n.data.type === type);
      if (existingNode) {
        SpotToast.error(`${nd.title} node already exists in this workflow`);
        return;
      }
    }

    const initialPosition =
      isTutorial && type === "InputNode" ? { x: 300, y: 400 } : position;
    const getRandomNumber = () => Math.floor(Math.random() * 100 + 1);

    const newConfig = structuredClone(nd);

    const newNode: NoxusReactflowNode = {
      id: uuidv4(),
      type: "baseNode",
      position: initialPosition ||
        reactFlowInstance?.screenToFlowPosition({
          x: (window?.innerWidth / 2 || 500) + getRandomNumber(),
          y: (window?.innerHeight / 2 || 500) + getRandomNumber(),
        }) || {
          x: 500 + getRandomNumber(),
          y: 500 + getRandomNumber(),
        },
      deletable: nd?.deletable ?? true,
      data: {
        config: { ...newConfig, ...config },
        name:
          config?.subflow_config?.workflow_name ||
          config?.agent_config?.assistant_name ||
          nd?.title,
        type,
        plugin_name: nd?.plugin_name,
      },
    };

    if (newNode.data.config.config) {
      Object.entries(newNode.data.config.config).forEach(([key, value]) => {
        const v = value as any;
        if (v.default !== undefined) {
          form?.setValue(`${newNode.id}.${key}`, v.default);
        }
      });
    }

    //------- END Variable size connectors ---------

    if (type === "InputNode" || type === "OutputNode") {
      const nodes_names = nodes
        .filter(
          (n) => n.data.type === "InputNode" || n.data.type === "OutputNode"
        )
        .map((n) => n.data.name) as string[];

      const maxValue = findMaxStringValue(
        nodes_names,
        type === "InputNode" ? /^Input (\d+)$/ : /^Output (\d+)$/
      );
      const new_name = `${type === "InputNode" ? "Input" : "Output"} ${maxValue ? maxValue + 1 : 1}`;
      newNode.data.name = new_name;

      form?.setValue(`${newNode.id}.label`, new_name);
      form?.setValue(`${newNode.id}.type`, "str");
    }

    // Picker selections carry the binding needed to resolve the node's config.
    if (config?.subflow_config?.workflow_id) {
      try {
        // Fetch the subflow IO - use the actual node type for config endpoint
        const subflow = await NodesService.getNodeConfig({
          path: {
            group_id: groupId,
            node_id: type,
          },
          body: {
            workflow_id: (
              config?.subflow_config as {
                workflow_id: string;
                workflow_name: string;
              }
            ).workflow_id,
          },
        });

        newNode.data = {
          ...newNode.data,
          config: {
            ...newNode.data.config,
            config: {
              ...newNode.data.config.config,
              ...(subflow.data as any).config,
            },
            inputs: (subflow.data as any).inputs,
            outputs: (subflow.data as any).outputs,
          },
        };

        const wfid = (
          config?.subflow_config as {
            workflow_id: string;
            workflow_name: string;
          }
        ).workflow_id;

        form?.setValue(`${newNode.id}.workflow_id`, wfid);
      } catch (e) {
        console.error(e);
        SpotToast.error("There was an error adding this node");
        return;
      }
    }

    if (config?.agent_config?.assistant_id) {
      try {
        const agentConfig = await NodesService.getNodeConfig({
          path: {
            group_id: groupId,
            // Hit the same node-config endpoint the dropped node will use,
            // so the response shape matches the V1 vs V2 schema we're about
            // to register.
            node_id: type,
          },
          body: {
            assistant_id: (config?.agent_config as { assistant_id: string })
              ?.assistant_id,
          },
        });

        newNode.data = {
          ...newNode.data,
          config: {
            ...newNode.data.config,
            config: {
              ...newNode.data.config.config,
              ...(agentConfig.data as any).config,
            },
          },
        };

        newNode.data.name =
          (config?.agent_config as { assistant_name?: string })
            ?.assistant_name || nd?.title;

        form?.setValue(
          `${newNode.id}.assistant_id`,
          (config?.agent_config as { assistant_id: string })?.assistant_id
        );
      } catch (e) {
        console.error(e);
        SpotToast.error("There was an error adding this agent node");
        return;
      }
    }

    if (config?.plugin_id && config?.node_name) {
      try {
        // Resolve the plugin node's shape (inputs/outputs/config) from its
        // manifest via the same node-config endpoint the dropped node uses.
        const pluginConfig = await NodesService.getNodeConfig({
          path: { group_id: groupId, node_id: type },
          body: {
            plugin_id: (config as { plugin_id?: string })?.plugin_id,
            node_name: (config as { node_name?: string })?.node_name,
          },
        });

        newNode.data = {
          ...newNode.data,
          config: {
            ...newNode.data.config,
            config: {
              ...newNode.data.config.config,
              ...(pluginConfig.data as any).config,
            },
            inputs: (pluginConfig.data as any).inputs,
            outputs: (pluginConfig.data as any).outputs,
          },
        };

        newNode.data.name =
          (config as { plugin_display_name?: string })?.plugin_display_name ||
          nd?.title;

        form?.setValue(
          `${newNode.id}.plugin_id`,
          (config as { plugin_id?: string })?.plugin_id
        );
        form?.setValue(
          `${newNode.id}.node_name`,
          (config as { node_name?: string })?.node_name
        );
      } catch (e) {
        console.error(e);
        SpotToast.error("There was an error adding this plugin node");
        return;
      }
    }

    // Wrapping up...
    let nds: NoxusReactflowNode[] = [];
    nds = [...nodes, newNode];

    setNodes(nds);

    // debouncedSave({ nds });

    // analytics_track(ANALYTICS_EVENTS.WORKFLOW_ADD_MODULE, {
    //   node_type: type,
    // });
    analytics_track(ANALYTICS_EVENTS.WORKFLOW_ADD_MODULE, {
      node_type: type,
    });

    setHasQueueToUpdateConnectors(newNode.id);
    setHasQueueToSave(true);
    set(saveStateAtom, {
      timestamp: new Date().toISOString(),
      status: "unsaved_changes",
    });

    return newNode.id;
  }
);

export const saveWorkflowAtom = atom(
  null,
  async (
    get,
    set,
    args: {
      isForceSave?: boolean;
      formValues: Record<string, any>;
      spotGroupId: string;
      workflowId: string;
      queryClient?: any;
    }
  ) => {
    const { formValues, spotGroupId, workflowId, queryClient } = args;

    set(saveStateAtom, {
      timestamp: new Date().toISOString(),
      status: "saving",
    });

    const nodes = get(nodesAtom);
    const edges = get(edgesAtom);
    const hash = get(saveWorkflowHashAtom);

    const newWorkflowReq = {
      definition: createFlowDefinition(
        nodes,
        edges,
        formValues,
        get(runSettingsAtom)
      ).mainFlowDefinition,
    };

    const req: UpdateWorkflowRequest = {
      definition: newWorkflowReq.definition,
      workflow_hash: hash,
    };

    try {
      const result = await WorkflowService.updateWorkflow({
        path: {
          group_id: spotGroupId,
          workflow_id: workflowId,
        },
        body: req,
      });
      set(saveStateAtom, {
        timestamp: new Date().toISOString(),
        status: "saved",
      });
      if (queryClient) {
        queryClient.invalidateQueries({
          queryKey: ["WORKFLOWS"],
        });
        queryClient.invalidateQueries({
          queryKey: ["WORKFLOWS_ID"],
        });
      }
    } catch (e) {
      console.error(e);
      SpotToast.error("There was an error saving the workflow");
      set(saveStateAtom, {
        timestamp: new Date().toISOString(),
        status: "errored",
      });
      return;
    }
  }
);

export const draftInputsAtom = atom<Record<string, any>>({});

export const isTutorialAtom = atom(false);
export const showTutorialModalAtom = atom(false);

const forceSaveAtom = atom(false);

export const redirectTutorialAtom = atom(false);

export const isSavingWorkflowAtom = atom(false);

// Run modal state for template sidenav detection
export const isRunModalOpenAtom = atom(false);

// Record Studio open state — editor shortcuts are suppressed while open
export const isRecordStudioOpenAtom = atom(false);

// Template deployment loading state
export const isTemplateDeploymentLoadingAtom = atom(false);
export const templateDeploymentMessageIndexAtom = atomWithStorage(
  "template-deployment-message-index",
  0
);
export const templateDeploymentProgressAtom = atomWithStorage(
  "template-deployment-progress",
  0
);

const workflowValidityExceptionsAtom = atom<Record<string, any>>({});

export const hoveredPartialRunNodeAtom = atom<string | undefined>(undefined);
export const hoveredNodeAtom = atom<string | undefined>(undefined);

export const partialRunNodesAtom = atom<string[]>([]);
export const singleNodeAtom = atom<string | undefined>(undefined);
export const allowPartialRunAtom = atom<boolean>(true);

export const pendingFocusNodeIdAtom = atom<string | null>(null);

export const highlightedNodeIdAtom = atom<string | null>(null);

export const hoveredPartialWorkflowStructureAtom = atom<
  IPartialWorkflowStructure | undefined
>(undefined);

export const hasQueueToUpdateConnectorsAtom = atom<string | null>(null);
export const hasQueueToSaveAtom = atom<boolean>(false);

export const flushEditorSaveSignalAtom = atom<number>(0);

export const renamingNodeIdAtom = atom<string | null>(null);

export const updateNodeCredentialIdsAtom = atom(
  null,
  (
    get,
    set,
    args: { nodeId: string; integrationId: string; credentialId: string }
  ) => {
    const { nodeId, integrationId, credentialId } = args;
    const nodes = get(nodesAtom);
    const node = nodes.find((n) => n.id === nodeId);
    if (!node) return;

    const copyNode = structuredClone(node);
    copyNode.data.credential_ids = {
      ...(copyNode.data.credential_ids || {}),
      [integrationId]: credentialId,
    };

    set(updateNodeAtom, { id: nodeId, node: copyNode });
    set(hasQueueToSaveAtom, true);
  }
);

// Patch multiple credential IDs at once. A null value removes the key.
export const patchNodeCredentialIdsAtom = atom(
  null,
  (
    get,
    set,
    args: { nodeId: string; updates: Record<string, string | null> }
  ) => {
    const { nodeId, updates } = args;
    const nodes = get(nodesAtom);
    const node = nodes.find((n) => n.id === nodeId);
    if (!node) return;

    const copyNode = structuredClone(node);
    const current: Record<string, string> = {
      ...(copyNode.data.credential_ids || {}),
    };

    for (const [integrationId, credentialId] of Object.entries(updates)) {
      if (credentialId === null) {
        delete current[integrationId];
      } else {
        current[integrationId] = credentialId;
      }
    }

    copyNode.data.credential_ids = current;
    set(updateNodeAtom, { id: nodeId, node: copyNode });
    set(hasQueueToSaveAtom, true);
  }
);

/* ─── Group / Ungroup atoms ─── */

export const groupSelectedNodesAtom = atom(null, (get, set) => {
  const nodes = get(nodesAtom);

  // Selected top-level nodes (non-notes, non-children)
  const selectedTopLevel = nodes.filter(
    (n) => n.selected && n.type !== "note" && !n.parentId
  );

  // Separate selected groups from regular nodes
  const selectedGroups = selectedTopLevel.filter((n) => n.type === "groupNode");
  const selectedRegular = selectedTopLevel.filter(
    (n) => n.type !== "groupNode"
  );

  // Flatten: dissolve selected groups and collect their children
  const dissolvedGroupIds = new Set(selectedGroups.map((g) => g.id));
  const childrenOfDissolvedGroups = nodes.filter(
    (n) => n.parentId && dissolvedGroupIds.has(n.parentId)
  );

  // Convert children to absolute positions
  const absoluteChildren = childrenOfDissolvedGroups.map((n) => {
    const parentGroup = selectedGroups.find((g) => g.id === n.parentId);
    return {
      ...n,
      parentId: undefined,
      extent: undefined,
      position: {
        x: n.position.x + (parentGroup?.position.x ?? 0),
        y: n.position.y + (parentGroup?.position.y ?? 0),
      },
    };
  });

  // Merge: regular selected nodes + children from dissolved groups
  const nodesToGroup = [...selectedRegular, ...absoluteChildren];
  if (nodesToGroup.length < 2) return;

  const { minX, minY, maxX, maxY } = getBoundingBox(nodesToGroup);

  const groupX = minX - GROUP_PADDING;
  const groupY = minY - GROUP_PADDING - 20; // extra space for label
  const groupWidth = maxX - minX + GROUP_PADDING * 2;
  const groupHeight = maxY - minY + GROUP_PADDING * 2 + 20;

  const groupId = uuidv4();

  const groupNode: NoxusReactflowNode = {
    id: groupId,
    type: "groupNode",
    position: { x: groupX, y: groupY },
    width: groupWidth,
    height: groupHeight,
    style: {
      width: groupWidth,
      height: groupHeight,
      zIndex: -1,
    },
    data: {
      name: "Group",
      type: "GroupNode",
      config: {},
    },
  };

  const nodeIdsToGroup = new Set(nodesToGroup.map((n) => n.id));

  // Remove dissolved groups, update children with new parent
  const updatedNodes = nodes
    .filter((n) => !dissolvedGroupIds.has(n.id))
    .map((n) => {
      // Nodes that were children of dissolved groups — use absolute position
      const absChild = absoluteChildren.find((ac) => ac.id === n.id);
      if (absChild) {
        return {
          ...absChild,
          parentId: groupId,
          extent: "parent" as const,
          position: {
            x: absChild.position.x - groupX,
            y: absChild.position.y - groupY,
          },
          selected: false,
        };
      }
      // Regular selected nodes
      if (nodeIdsToGroup.has(n.id)) {
        return {
          ...n,
          parentId: groupId,
          extent: "parent" as const,
          position: {
            x: n.position.x - groupX,
            y: n.position.y - groupY,
          },
          selected: false,
        };
      }
      return n;
    });

  // React Flow requires parent nodes to appear before children in the array
  const nonChildNodes = updatedNodes.filter((n) => !nodeIdsToGroup.has(n.id));
  const childNodes = updatedNodes.filter((n) => nodeIdsToGroup.has(n.id));
  set(nodesAtom, [...nonChildNodes, groupNode, ...childNodes]);
  set(hasQueueToSaveAtom, true);
  set(saveStateAtom, {
    timestamp: new Date().toISOString(),
    status: "unsaved_changes",
  });

  return groupId;
});

export const ungroupNodesAtom = atom(
  null,
  (get, set, args: { groupId: string }) => {
    const { groupId } = args;
    const nodes = get(nodesAtom);
    const groupNode = nodes.find((n) => n.id === groupId);
    if (!groupNode) return;

    const updatedNodes = nodes
      .filter((n) => n.id !== groupId)
      .map((n) => {
        if (n.parentId === groupId) {
          return {
            ...n,
            parentId: undefined,
            extent: undefined,
            position: {
              x: n.position.x + groupNode.position.x,
              y: n.position.y + groupNode.position.y,
            },
          };
        }
        return n;
      });

    set(nodesAtom, updatedNodes);
    set(hasQueueToSaveAtom, true);
    set(saveStateAtom, {
      timestamp: new Date().toISOString(),
      status: "unsaved_changes",
    });
  }
);

/* ─── Convert to subflow atoms ─── */

export const pendingConvertAtom = atom<
  { type: "group"; groupId: string } | { type: "selection" } | null
>(null);

export const convertGroupToSubflowAtom = atom(
  null,
  async (
    get,
    set,
    args: {
      groupId: string;
      form: UseFormReturn;
      spotGroupId: string;
      nodesDefinition: NodeDefinition[] | undefined;
    }
  ) => {
    const { groupId, form, spotGroupId, nodesDefinition } = args;
    const nodes = get(nodesAtom);
    const edges = get(edgesAtom);

    const groupNode = nodes.find((n) => n.id === groupId);
    if (!groupNode) return;

    const childIds = new Set(
      nodes.filter((n) => n.parentId === groupId).map((n) => n.id)
    );
    const childNodes = nodes
      .filter((n) => childIds.has(n.id))
      .map((n) => ({ ...n, parentId: undefined, extent: undefined }));
    if (childNodes.length === 0) return;

    const { incoming, outgoing, internal } = classifyEdges(edges, childIds);
    const groupLabel = form.getValues(`${groupId}.label`);
    const name = groupLabel || "Subflow";
    const groupW = groupNode.measured?.width ?? groupNode.width ?? 300;
    const groupH = groupNode.measured?.height ?? groupNode.height ?? 200;

    const result = await buildSubflowAndInvokeNode({
      nodesToExtract: childNodes,
      incomingEdges: incoming,
      outgoingEdges: outgoing,
      internalEdges: internal,
      name,
      invokePosition: {
        x: groupNode.position.x + groupW / 2 - 100,
        y: groupNode.position.y + groupH / 2 - 40,
      },
      form,
      spotGroupId,
      nodesDefinition,
    });
    if (!result) return;

    const remainingNodes = nodes.filter(
      (n) => n.id !== groupId && !childIds.has(n.id)
    );
    const remainingEdges = edges.filter(
      (e) => !result.boundaryEdgeIds.has(e.id)
    );

    set(nodesAtom, [...remainingNodes, result.invokeNode]);
    set(edgesAtom, [...remainingEdges, ...result.newEdges]);
    set(hasQueueToSaveAtom, true);
    set(saveStateAtom, {
      timestamp: new Date().toISOString(),
      status: "unsaved_changes",
    });
    SpotToast.success("Group converted to subflow");
  }
);

export const convertSelectionToSubflowAtom = atom(
  null,
  async (
    get,
    set,
    args: {
      form: UseFormReturn;
      spotGroupId: string;
      nodesDefinition: NodeDefinition[] | undefined;
    }
  ) => {
    const { form, spotGroupId, nodesDefinition } = args;
    const nodes = get(nodesAtom);
    const edges = get(edgesAtom);

    const selectedNodes = nodes.filter(
      (n) =>
        n.selected && n.type !== "note" && n.type !== "groupNode" && !n.parentId
    );
    if (selectedNodes.length < 2) return;

    const selectedIds = new Set(selectedNodes.map((n) => n.id));
    const { incoming, outgoing, internal } = classifyEdges(edges, selectedIds);
    const { minX, minY, maxX, maxY } = getBoundingBox(selectedNodes);

    const result = await buildSubflowAndInvokeNode({
      nodesToExtract: selectedNodes,
      incomingEdges: incoming,
      outgoingEdges: outgoing,
      internalEdges: internal,
      name: "Subflow",
      invokePosition: {
        x: (minX + maxX) / 2 - 100,
        y: (minY + maxY) / 2 - 40,
      },
      form,
      spotGroupId,
      nodesDefinition,
    });
    if (!result) return;

    const remainingNodes = nodes.filter((n) => !selectedIds.has(n.id));
    const remainingEdges = edges.filter(
      (e) => !result.boundaryEdgeIds.has(e.id)
    );

    set(nodesAtom, [...remainingNodes, result.invokeNode]);
    set(edgesAtom, [...remainingEdges, ...result.newEdges]);
    set(hasQueueToSaveAtom, true);
    set(saveStateAtom, {
      timestamp: new Date().toISOString(),
      status: "unsaved_changes",
    });
    SpotToast.success("Selection converted to subflow");
  }
);
